import React, { useEffect, useState, useRef } from 'react';
import { useTheme } from '../ThemeContext';
import { Globe } from 'lucide-react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface SegmentData {
  symbol: string;
  fiscalYear: number;
  period: string;
  date: string;
  data: Record<string, number>;
}

interface GeographicSegmentsProps {
  symbol?: string;
}

const GeographicSegments: React.FC<GeographicSegmentsProps> = ({ symbol = 'AAPL' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [segmentData, setSegmentData] = useState<SegmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'annual' | 'quarter'>('annual');
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const base = (import.meta.env.VITE_APP_API_URL as string);
        const url = `${base}/segment-and-kpi?symbol=${symbol}&period=${viewMode}&segmentType=geographic`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to fetch geographic segment data');
        }

        const data = await response.json();
        const sorted = data.sort((a: SegmentData, b: SegmentData) => b.fiscalYear - a.fiscalYear);
        setSegmentData(sorted);
        setError(null);

        // Get non-zero segments from the MOST RECENT year only
        if (sorted.length > 0) {
          const mostRecentYear = sorted[0]; // Most recent year data
          const nonZeroSegmentsFromRecentYear = Object.keys(mostRecentYear.data).filter(region => {
            const value = mostRecentYear.data[region] || 0;
            return value > 0; // Only segments with non-zero values in the most recent year
          });

          if (nonZeroSegmentsFromRecentYear.length > 0) {
            setSelectedRegions([nonZeroSegmentsFromRecentYear[0]]); // Select first non-zero segment
          }
        }
      } catch (err) {
        console.error('Error fetching geographic segment data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [symbol, viewMode]);

  const formatCurrency = (value: number): string => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toLocaleString()}`;
  };

  // Intelligent number formatting based on data magnitude
  const getOptimalUnit = (): { divisor: number; unit: string; label: string } => {
    if (segmentData.length === 0) return { divisor: 1e9, unit: 'B', label: 'Billions USD' };

    // Find the maximum value across all regions and all periods
    let maxValue = 0;
    segmentData.forEach(item => {
      // If no regions are selected, check all available regions
      const regionsToCheck = selectedRegions.length > 0 ? selectedRegions : Object.keys(item.data);
      regionsToCheck.forEach(region => {
        const value = item.data[region] || 0;
        if (value > maxValue) maxValue = value;
      });
    });

    // Determine optimal unit based on maximum value
    if (maxValue >= 1e9) {
      return { divisor: 1e9, unit: 'B', label: 'Billions USD' };
    } else if (maxValue >= 1e6) {
      return { divisor: 1e6, unit: 'M', label: 'Millions USD' };
    } else {
      return { divisor: 1, unit: '', label: 'USD' };
    }
  };

  const formatNumber = (value: number): number => {
    const { divisor } = getOptimalUnit();
    return value / divisor;
  };

  // Calculate CAGR for a geographic region
  const calculateCAGR = (region: string): number => {
    if (segmentData.length < 2) return 0;

    const reversedData = [...segmentData].reverse(); // Chronological order
    const nonZeroPeriods = reversedData.filter(item => {
      const value = item.data[region] || 0;
      return value > 0;
    });

    if (nonZeroPeriods.length < 2) return 0;

    const startValue = nonZeroPeriods[0].data[region] || 0;
    const endValue = nonZeroPeriods[nonZeroPeriods.length - 1].data[region] || 0;
    const years = nonZeroPeriods.length - 1;

    if (startValue <= 0 || endValue <= 0 || years <= 0) return 0;

    const cagr = (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
    return Math.round(cagr * 10) / 10; // Round to 1 decimal place
  };

  const getGeographicRegions = (): string[] => {
    if (segmentData.length === 0) return [];

    // Get segments that have non-zero values in the MOST RECENT year only
    const mostRecentYear = segmentData[0]; // First item is most recent due to sorting
    const nonZeroSegmentsFromRecentYear = Object.keys(mostRecentYear.data).filter(region => {
      const value = mostRecentYear.data[region] || 0;
      return value > 0; // Only segments with non-zero values in the most recent year
    });

    return nonZeroSegmentsFromRecentYear;
  };

  // Prepare chart data for selected regions using Highcharts
  const prepareHighchartsOptions = () => {
    if (selectedRegions.length === 0) return null;

    const reversedData = [...segmentData].reverse(); // Chronological order
    const { unit, label } = getOptimalUnit(); // Get the optimal unit for this data

    // Get only regions that have non-zero values in the most recent year
    const allRegions = getGeographicRegions();

    // Filter out periods where ALL selected regions have zero values
    const filteredPeriods = reversedData.filter(item => {
      return selectedRegions.some(region => {
        const value = item.data[region] || 0;
        return value > 0;
      });
    });

    const categories = filteredPeriods.map(item =>
      viewMode === 'quarter'
        ? `${item.period} ${item.fiscalYear}`
        : `FY ${item.fiscalYear}`
    );

    // Define distinct colors for each region
    const regionColors = [
      '#3b82f6', // Blue
      '#8b5cf6', // Purple
      '#10b981', // Green
      '#f59e0b', // Orange
      '#ef4444', // Red
      '#06b6d4', // Cyan
      '#84cc16', // Lime
      '#f97316'  // Orange-red
    ];

    const series = allRegions.map((region, regionIndex) => {
      const regionColor = regionColors[regionIndex % regionColors.length];
      const isSelected = selectedRegions.includes(region);
      const cagr = calculateCAGR(region);
      const cagrText = cagr !== 0 ? ` (CAGR ${cagr > 0 ? '+' : ''}${cagr}%)` : '';

      const data = filteredPeriods.map((item) => {
        const value = item.data[region] || 0;
        // Only show data for selected regions, and exclude zero values completely
        if (!isSelected || value === 0) {
          return null;
        }
        return {
          y: formatNumber(value),
          color: regionColor,
          rawValue: value
        };
      });

      return {
        name: `${region}${cagrText}`,
        type: 'column',
        data: data,
        color: regionColor,
        visible: isSelected, // Only selected regions are visible
        showInLegend: true, // All regions show in legend
        dataLabels: {
          enabled: isSelected, // Only show labels for selected regions
          format: '${point.y:.2f}',
          style: {
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '11px',
            fontWeight: '600',
            textOutline: 'none'
          },
          y: -5
        }
      };
    });

    const options: Highcharts.Options = {
      chart: {
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'Arial, sans-serif'
        },
        height: 400
      },
      credits: {
        enabled: true,
        text: 'Powered by StockFortress',
        href: 'https://stockfortress.com',
        style: {
          fontSize: '10px',
          color: 'rgba(255, 255, 255, 0.4)'
        }
      },
      title: {
        text: ''
      },
      xAxis: {
        categories: categories,
        labels: {
          style: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: '11px'
          }
        },
        lineColor: isDark ? '#374151' : '#e5e7eb',
        tickColor: isDark ? '#374151' : '#e5e7eb'
      },
      yAxis: {
        title: {
          text: `Revenue (${label})`,
          style: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: '12px'
          }
        },
        labels: {
          format: '${value}',
          style: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: '11px'
          }
        },
        gridLineColor: isDark ? '#374151' : '#e5e7eb'
      },
      tooltip: {
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        style: {
          color: isDark ? '#ffffff' : '#000000'
        },
        useHTML: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const point = this.point as any;
          const seriesName = this.series.name;
          const regionName = seriesName.split(' (CAGR')[0]; // Extract original region name
          const cagr = calculateCAGR(regionName);
          const cagrText = cagr !== 0 ? `<br/>CAGR: <b>${cagr > 0 ? '+' : ''}${cagr}%</b>` : '';

          const formattedValue = this.y ? `$${this.y.toFixed(2)}${unit}` : 'N/A';

          return `<div style="padding: 5px;">
            ${regionName}: <b>${formattedValue}</b>${cagrText}
          </div>`;
        }
      },
      legend: {
        enabled: true,
        itemStyle: {
          color: isDark ? '#e5e7eb' : '#374151',
          fontSize: '12px'
        },
        itemHoverStyle: {
          color: isDark ? '#ffffff' : '#000000'
        },
        itemHiddenStyle: {
          color: isDark ? '#6b7280' : '#9ca3af' // Gray out hidden/unselected items
        }
      },
      plotOptions: {
        column: {
          borderRadius: 4,
          borderWidth: 0,
          groupPadding: 0.15,
          pointPadding: 0.1
        },
        series: {
          events: {
            legendItemClick: function () {
              const seriesName = this.name;
              const regionName = seriesName.split(' (CAGR')[0]; // Extract original region name
              setSelectedRegions(prev => {
                if (prev.includes(regionName)) {
                  return prev.filter(p => p !== regionName);
                } else {
                  return [...prev, regionName];
                }
              });
              return false; // Prevent default legend click behavior
            }
          }
        }
      },
      series: series as any
    };

    return options;
  };

  const handleRegionToggle = (region: string) => {
    setSelectedRegions(prev => {
      if (prev.includes(region)) {
        return prev.filter(p => p !== region);
      } else {
        // Limit to 3 regions for clarity
        if (prev.length >= 3) {
          return [...prev.slice(1), region];
        }
        return [...prev, region];
      }
    });

    // Scroll to chart when selecting
    setTimeout(() => {
      chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const geographicRegions = getGeographicRegions();
  const chartOptions = prepareHighchartsOptions();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 bg-theme-secondary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className={`ml-3 ${isDark ? 'text-theme-secondary' : 'text-gray-700'}`}>Loading geographic segments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 rounded-lg ${isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'} border m-4`}>
        <p className={`${isDark ? 'text-red-300' : 'text-red-700'}`}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-theme-secondary px-4 pb-4 pt-2 text-theme-primary">
      {/* Chart Section */}
      {selectedRegions.length > 0 && chartOptions && (
        <div ref={chartRef} className="bg-theme-secondary rounded-lg mb-4 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold mb-1">Revenue Trends by Region</h3>
              <p className="text-xs text-theme-secondary">
                All Geographic Segments - Values in {getOptimalUnit().label}
              </p>
            </div>
            {/* Annual/Quarterly Toggle moved inside chart container */}
            <div className="flex gap-1">
              <button
                onClick={() => setViewMode('annual')}
                className={`px-3 py-1 text-xs rounded-md btn ${viewMode === 'annual'
                    ? 'active'
                    : ''
                  }`}
              >
                Annual
              </button>
              <button
                onClick={() => setViewMode('quarter')}
                className={`px-3 py-1 text-xs rounded-md btn ${viewMode === 'quarter'
                    ? 'active'
                    : ''
                  }`}
              >
                Quarterly
              </button>
            </div>
          </div>

          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
          />
        </div>
      )}
    </div>
  );
};

export default GeographicSegments;
